export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

const STORAGE_KEY = "admin_auth_tokens";
const AUTH_TOKEN_KEY = "admin_auth_token"; // For backward compatibility

/**
 * Super-admin "view as agency admin" sessions live in sessionStorage, not
 * localStorage. sessionStorage is per-tab, so the impersonated session can
 * never overwrite the super admin's own login in their other tabs, and it
 * vanishes when the tab closes. Everything that reads or writes tokens goes
 * through `authStorage()`, so no caller has to know which one is active.
 */
export const IMPERSONATION_KEY = "admin_impersonation";

export interface ImpersonationInfo {
  sessionId: string;
  targetName: string | null;
  targetRole: string;
  actorName: string | null;
  /** ISO timestamp the impersonation token stops working. */
  expiresAt: string;
}

export function getImpersonation(): ImpersonationInfo | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationInfo) : null;
  } catch {
    return null;
  }
}

export function authStorage(): Storage {
  try {
    if (sessionStorage.getItem(IMPERSONATION_KEY)) return sessionStorage;
  } catch {
    // sessionStorage unavailable — fall through to localStorage.
  }
  return localStorage;
}

// Emit custom events for token state changes
function emitTokenEvent(eventName: string, detail?: any) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
}

export function saveTokens(tokens: Tokens): void {
  const storage = authStorage();
  // Save in new format
  storage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  // Also save access token in old format for backward compatibility
  storage.setItem(AUTH_TOKEN_KEY, tokens.accessToken);
  emitTokenEvent('tokensSaved', { tokens });
}

export function getTokens(): Tokens | null {
  const storage = authStorage();
  // First try to get from new format
  const json = storage.getItem(STORAGE_KEY);
  if (json) {
    try {
      return JSON.parse(json) as Tokens;
    } catch {
      // Invalid JSON, continue to fallback
    }
  }

  // Fallback to old format (just access token)
  const accessToken = storage.getItem(AUTH_TOKEN_KEY);
  if (accessToken) {
    // We don't have refresh token in old format, return null
    // This will trigger a re-login when refresh is needed
    return null;
  }

  return null;
}

export function getAccessToken(): string | null {
  const tokens = getTokens();
  if (tokens) {
    return tokens.accessToken;
  }
  // Fallback to old format
  return authStorage().getItem(AUTH_TOKEN_KEY);
}

export function clearTokens(): void {
  const storage = authStorage();
  storage.removeItem(STORAGE_KEY);
  storage.removeItem(AUTH_TOKEN_KEY); // Also clear old format
  emitTokenEvent('tokensCleared');
}

/**
 * Begin an impersonated session in this tab. The flag is set *before* the
 * tokens are saved so they land in sessionStorage.
 */
export function startImpersonatedSession(info: ImpersonationInfo, accessToken: string): void {
  sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(info));
  // No refresh token is issued for impersonation; an empty one makes the
  // refresh path fail fast instead of sending the access token as a refresh token.
  saveTokens({ accessToken, refreshToken: "" });
}

/** Drop the impersonated session from this tab (does not touch localStorage). */
export function clearImpersonatedSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch {
    // ignore
  }
}

// Helper to migrate from old format to new format
export function migrateTokens(accessToken: string, refreshToken: string): void {
  saveTokens({ accessToken, refreshToken });
}
